import { useMemo } from 'react';

import { FieldValidationProps, ValidationResultType } from '../../hooks/useFieldValidation';
import useSettings from '../../hooks/useSettings';
import { CountryCode } from '../../types/CountryCode';
import TextInputField from '../TextInputField/TextInputField';

type PhoneFieldProps = {
    name: string;
    label?: string;
};

const formatMap = new Map([
    ['SE', '^\\d{10}$'],
    ['NO', '^\\d{8}$'],
    ['DK', '^\\d{8}$'],
    ['FI', '^\\d{10}$'],
]);

const formatErrorKeyMap = new Map([
    ['SE', 'validation.error.phone.format.se'],
    ['NO', 'validation.error.phone.format.no'],
    ['DK', 'validation.error.phone.format.dk'],
    ['FI', 'validation.error.phone.format.fi'],
]);

const cleanPhoneNumber = (phoneNumber: string | undefined): string | undefined => {
    return phoneNumber?.replace(/[^0-9\-+]/g, '');
};

const phoneValidationService = (countryCode: CountryCode, value?: string): Promise<ValidationResultType> => {
    const isValid = value !== undefined;

    return Promise.resolve({
        valid: isValid,
        errorMessageKey: isValid ? undefined : formatErrorKeyMap.get(countryCode.toUpperCase()),
    });
};

const PhoneField = (props: PhoneFieldProps) => {
    const { countryCode } = useSettings();

    const validationProps: FieldValidationProps = useMemo(
        () => ({
            format: formatMap.get(countryCode.toUpperCase()),
            formatErrorKey: formatErrorKeyMap.get(countryCode.toUpperCase()),
            customValidation: (value: string | undefined) => phoneValidationService(countryCode, value),
        }),
        [countryCode],
    );

    return (
        <TextInputField
            type="tel"
            validation={validationProps}
            formatting={{
                format: (value: any) => value,
                clean: cleanPhoneNumber,
            }}
            {...props}
        />
    );
};

export default PhoneField;
